<template>
  <div
    ref="container"
    class="relative w-full h-full"
    :class="{ 'bg-gray-100 dark:bg-gray-800': !loaded }"
  >
    <img
      v-if="blobUrl && !error"
      :src="blobUrl"
      :alt="alt"
      class="w-full h-full object-cover"
      @load="onImageLoaded"
      @error="onImageError"
    />
    <div
      v-if="!blobUrl && !error && !isLoading"
      class="absolute inset-0 flex items-center justify-center"
    >
      <!-- Placeholder initial avant le chargement -->
      <ImageIcon class="w-10 h-10 text-gray-300 dark:text-gray-600" />
    </div>
    <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
      <div
        class="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"
      ></div>
    </div>
    <div
      v-if="error"
      class="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
    >
      <AlertTriangleIcon class="w-8 h-8 mb-2" />
      <span class="text-xs text-center px-2">Image non<br />disponible</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { thumbnailCache } from '@/services/thumbnailCacheService'
import { AlertTriangleIcon, ImageIcon } from 'lucide-vue-next'
import { onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  src: string
  alt: string
}>()

const emit = defineEmits<{
  (e: 'load'): void
  (e: 'error', error: Error | Event): void
}>()

const container = ref<HTMLElement | null>(null)
const blobUrl = ref<string>('')
const loaded = ref(false)
const error = ref(false)
const isLoading = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const retryCount = ref(0)
const maxRetries = 1
const retryTimeout = ref<number | null>(null)
const abortController = ref<AbortController | null>(null)

const loadImage = async (forceRetry = false) => {
  if (!props.src) {
    error.value = true
    return
  }

  // Si on force un retry, réinitialiser l'état d'erreur
  if (forceRetry) {
    error.value = false
    blobUrl.value = ''
    loaded.value = false
  }

  // Ne pas recharger si déjà chargé et pas de retry forcé
  if (loaded.value && !forceRetry) return

  // Si erreur et on a déjà fait tous les retries, ne plus essayer
  if (error.value && retryCount.value >= maxRetries && !forceRetry) return

  // Annuler toute requête précédente
  if (abortController.value) {
    abortController.value.abort()
  }

  // Créer un nouveau AbortController pour cette requête
  abortController.value = new AbortController()

  isLoading.value = true
  try {
    const cachedUrl = await thumbnailCache.getThumbnail(props.src, abortController.value.signal)
    if (cachedUrl) {
      blobUrl.value = cachedUrl
      retryCount.value = 0 // Réinitialiser le compteur en cas de succès
    } else {
      throw new Error('Image not found in cache or network')
    }
  } catch (e) {
    // Si la requête a été annulée, ne pas traiter comme une erreur
    if (e instanceof Error && e.name === 'AbortError') {
      return
    }
    // Ne pas retry pour les erreurs 404
    const err = e instanceof Error ? e : new Error(String(e))
    const is404 = err.message.includes('404') || err.message.includes('not found')
    if (is404) {
      console.warn(`[LazyThumbnail] Image non trouvée (404) pour ${props.src}, pas de retry`)
      onImageError(err)
      return
    }
    // Si on n'a pas encore atteint le max de retries, réessayer
    if (retryCount.value < maxRetries) {
      retryCount.value++
      const delay = 1000 * Math.pow(2, retryCount.value - 1) // Délai exponentiel
      console.log(
        `[LazyThumbnail] 🔄 Retry ${retryCount.value}/${maxRetries} après ${delay}ms pour ${props.src}`
      )
      retryTimeout.value = window.setTimeout(() => {
        loadImage(true)
      }, delay)
    } else {
      onImageError(err)
    }
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.src,
  (newSrc, oldSrc) => {
    if (newSrc !== oldSrc) {
      // Annuler toute requête en cours
      if (abortController.value) {
        abortController.value.abort()
        abortController.value = null
      }
      // Annuler tout retry en cours
      if (retryTimeout.value !== null) {
        clearTimeout(retryTimeout.value)
        retryTimeout.value = null
      }
      blobUrl.value = ''
      loaded.value = false
      error.value = false
      isLoading.value = false
      retryCount.value = 0
    }
    loadImage()
  },
  { immediate: true }
)

function onImageLoaded() {
  loaded.value = true
  isLoading.value = false
  emit('load')
}

function onImageError(e: Error | Event) {
  // Ne pas retry pour les erreurs 404 (image n'existe pas)
  const err = e instanceof Error ? e : new Error('Failed to load image')
  const is404 = err.message.includes('404') || err.message.includes('not found')
  // Si l'erreur vient de l'élément <img> (blob URL invalide), réessayer une seule fois
  if (blobUrl.value && retryCount.value < maxRetries && !is404) {
    retryCount.value++
    const delay = 1000 * Math.pow(2, retryCount.value - 1) // Délai exponentiel
    console.log(
      `[LazyThumbnail] 🔄 Erreur chargement <img>, retry ${retryCount.value}/${maxRetries} après ${delay}ms pour ${props.src}`
    )
    error.value = false // Réinitialiser l'erreur pour permettre le retry
    isLoading.value = true
    // Révoquer l'URL blob invalide
    if (blobUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl.value)
    }
    blobUrl.value = ''
    retryTimeout.value = window.setTimeout(async () => {
      // Invalider uniquement cette entrée du cache pour forcer le re-téléchargement
      await thumbnailCache.invalidateEntry(props.src)
      loadImage(true)
    }, delay)
    return
  }

  // Si tous les retries sont épuisés ou erreur 404, afficher l'erreur
  error.value = true
  isLoading.value = false
  emit('error', err)
}

onMounted(() => {
  if (!container.value) return

  observer.value = new IntersectionObserver(
    entries => {
      if (entries[0].isIntersecting) {
        loadImage()
        observer.value?.disconnect()
      }
    },
    {
      root: null,
      rootMargin: '100px', // Augmenter légèrement pour précharger les images proches du viewport
      threshold: 0.01, // Réduire le seuil pour déclencher plus tôt
    }
  )

  observer.value.observe(container.value)
})

onUnmounted(() => {
  observer.value?.disconnect()
  // Nettoyer le timeout de retry si présent
  if (retryTimeout.value !== null) {
    clearTimeout(retryTimeout.value)
    retryTimeout.value = null
  }
  // Annuler toute requête en cours
  if (abortController.value) {
    abortController.value.abort()
    abortController.value = null
  }
})
</script>
